import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { defaultScriptValue } from "@/utils/utils";

const mapSchemaSlice = createSlice({
  name: "mapSchema",
  initialState: {
    mapType: "",
    connectionId: "",
    artifactType: "",
    dataSourceType: "",
    mappings: {},
    popUp: false,
    error: null,
    status: "idle",
    currentOutboundElement: {},
    currentArtifactDictionaryDataId: null,
    artifactComplex: [],
    isMappingClicked: false,
    selectedSchema: {},
    outboundSchemaTable: [], // for outbound mapping element usermap/loop editing
    allOutboundSchemaTables: [], // all tables for cross-table navigation
    artifactHeaderData: [],
    artifactDetailData: [],
    artifactHeaderOptionalField: [],
    artifactDetailOptionalField: [],
    artifactHeaderConsolidated: [],
    artifactDetailConsolidated: [],
    loopRowsList: [],
    stagedOutboundEdits: {}, //dictionary of edits for batch saving
  },
  reducers: {
    setMapType: (state, action) => {
      const { mapType } = action.payload;
      state.mapType = mapType;
    },
    setConnectionId: (state, action) => {
      const { connectionId } = action.payload;
      state.connectionId = connectionId;
    },
    setDataSourceType: (state, action) => {
      const { dataSourceType } = action.payload;
      state.dataSourceType = dataSourceType;
    },
    setPopUp: (state, action) => {
      const { popUp } = action.payload;
      state.popUp = popUp;
    },
    setArtifactComplex: (state, action) => {
      const { artifactComplex } = action.payload;
      state.artifactComplex = artifactComplex;
    },
    setArtifactDictionaryDataId: (state, action) => {
      const { artifactDictionaryDataId } = action.payload;
      state.currentArtifactDictionaryDataId = artifactDictionaryDataId;
    },
    updateFields: (state, action) => {
      const { key, value } = action.payload;
      let draftState = JSON.parse(JSON.stringify(current(state)));
      draftState.mappings[key] = value;
      state.mappings = draftState.mappings;
    },
    resetFields: (state) => {
      state.mappings = {};
    },
    updateOutboundElementField: (state, action) => {
      const { key, value } = action.payload;
      let draftState = JSON.parse(JSON.stringify(current(state)));
      draftState.currentOutboundElement[key] = value;
      state.currentOutboundElement = draftState.currentOutboundElement;

      //piggyback update to staged edits for batch saving
      const rowKey = draftState.currentOutboundElement.id;
      state.stagedOutboundEdits[rowKey] = {
        ...draftState.currentOutboundElement,
      }; //shallow copy to avoid reference issues?
    },

    updateOutboundElement: (state, action) => {
      const { element } = action.payload;

      if (!element) {
        state.currentOutboundElement = {};
        return;
      }

      //prefers the staged edit if it exists
      const rowKey = element.id;
      state.currentOutboundElement =
        state.stagedOutboundEdits[rowKey] || element;
    },
    setLoopRowsList: (state, action) => {
      const { loopRowsList } = action.payload;
      state.loopRowsList = loopRowsList;
    },
    setIsMappingClicked: (state, action) => {
      const { isMappingClicked } = action.payload;
      state.isMappingClicked = isMappingClicked;
    },
    setSelectedSchema: (state, action) => {
      const { selectedSchema } = action.payload;
      state.selectedSchema = selectedSchema;
    },
    setOutboundSchemaTable: (state, action) => {
      const { outboundSchemaTable } = action.payload;
      state.outboundSchemaTable = outboundSchemaTable;
    },
    setAllOutboundSchemaTables: (state, action) => {
      const { allOutboundSchemaTables } = action.payload;
      state.allOutboundSchemaTables = allOutboundSchemaTables;
    },
    setArtifactHeaderInfo: (state, action) => {
      const { artifactHeaderData } = action.payload;
      state.artifactHeaderData = artifactHeaderData;
    },
    setArtifactDetailInfo: (state, action) => {
      const { artifactDetailData } = action.payload;
      state.artifactDetailData = artifactDetailData;
    },
    setArtifactHeaderOptionalFieldInfo: (state, action) => {
      const { artifactHeaderOptionalField } = action.payload;
      state.artifactHeaderOptionalField = artifactHeaderOptionalField;
    },
    setArtifactDetailOptionalFieldInfo: (state, action) => {
      const { artifactDetailOptionalField } = action.payload;
      state.artifactDetailOptionalField = artifactDetailOptionalField;
    },
    setArtifactHeaderConsolidatedInfo: (state, action) => {
      const { artifactHeaderConsolidated } = action.payload;
      state.artifactHeaderConsolidated = artifactHeaderConsolidated;
    },
    setArtifactDetailConsolidatedInfo: (state, action) => {
      const { artifactDetailConsolidated } = action.payload;
      state.artifactDetailConsolidated = artifactDetailConsolidated;
    },
    addOptionalField: (state, action) => {
      state.artifactHeaderOptionalField.push(...action.payload);
      state.artifactHeaderConsolidated = state.artifactHeaderData.concat(
        state.artifactHeaderOptionalField
      );
    },

    //reducers for managing staged edits for batch saving
    addItemToEditBatch: (state, action) => {
      const { key, value } = action.payload;
      //key should be element ID, value should be the entire element object, with all its latest fields
      state.stagedOutboundEdits[key] = value;
    },
    removeItemFromEditBatch: (state, action) => {
      const { key } = action.payload;
      delete state.stagedOutboundEdits[key];
    },
    clearItemsFromEditBatch: (state, action) => {
      state.stagedOutboundEdits = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getArtifactType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getArtifactType.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(getArtifactType.fulfilled, (state, action) => {
        state.status = "loaded";
        state.artifactType = action.payload;
      });
  },
});

export const getArtifactType = createAsyncThunk(
  "mapschema/getArtifactType",
  async (data) => {
    const response = await axios.get("/mapschema/artifact-type", {
      params: {
        operationId: data.operationId,
        mapType: data.mapType,
      },
    });
    return response.data;
  }
);

export const testScript = async (dispatch, script, mapValue) => {
  try {
    if (!script || script.length === 0) {
      script = defaultScriptValue;
    }
    const response = await axios.post(
      "mapschema/inbound/validate-script",
      {
        Script: script,
        MapValue: mapValue,
      },
      null
    );
    if (await response.data) {
      dispatch(
        createAlert({
          status: "Success",
          alertType: "result",
          message: `The script was tested successfully, result: ${response.data}.`,
          buttonLabel: "Close",
        })
      );
    }
  } catch (err) {
    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        message: err.response?.data ?? err.message,
        buttonLabel: "Close",
      })
    );
  }
};

//for the new edit schema screen
export const saveMappingInfo =
  (
    mapType,
    connectionId,
    artifactType,
    dataSourceType,
    connectionArtifactSchemaId,
    onBack,
    heapIOIsEditMode
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const mappingObj = {
      mapType: mapType,
      connectionId: connectionId,
      artifactType: artifactType,
      dataSourceType: dataSourceType,
      connectionArtifactSchemaId: connectionArtifactSchemaId,
      mappings: state.tempInfo.mapSchema.mappings,
    };
    let validationMessage = "";

    const mappingArea = mappingObj.mappings.mappingArea;
    const mappingSegment = mappingObj.mappings.mappingSegment;
    const valuePosition = mappingObj.mappings.valuePosition;
    const occurrence = mappingObj.mappings.occurrence;
    const targetField = mappingObj.mappings.targetField;

    if (mappingArea === undefined) {
      validationMessage += "Mapping Area must be selected!\n";
    }
    if (mappingSegment === undefined || mappingSegment.trim() === "") {
      validationMessage += "Mapping Segment must not be blank!\n";
    }
    if (
      isNaN(valuePosition) ||
      valuePosition === undefined ||
      valuePosition.trim() === ""
    ) {
      validationMessage +=
        "Value Position must not be blank and must be a number!\n";
    }
    if (
      isNaN(occurrence) ||
      occurrence === undefined ||
      occurrence.trim() === ""
    ) {
      validationMessage +=
        "Occurrence must not be blank and must be a number!\n";
    }
    if (targetField === undefined || targetField.trim() === "") {
      validationMessage += "Document field not selected!\n";
    }
    if (validationMessage !== "") {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: validationMessage,
          buttonLabel: "Close",
        })
      );
      return;
    }

    try {
      const response = await axios.post(
        "/mapschema/inbound/save",
        mappingObj,
        null
      );
      if (response.data) {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            message: "Mapping saved successfully!",
            buttonLabel: "Close",
          })
        );
        dispatch(resetFields());
        onBack();

        // heap.io
        if (heapIOIsEditMode) {
          window.heap.track("Inbound Mapping - Edit Map");
        } else {
          window.heap.track("Inbound Mapping - Save New Map");
        }
      }
    } catch (err) {}
    return false;
  };

export const saveMapping = () => async (dispatch, getState) => {
  const state = getState();
  let mappingObj = {
    mapType: state.tempInfo.mapSchema.mapType,
    connectionId: state.tempInfo.mapSchema.connectionId,
    artifactType: state.tempInfo.mapSchema.artifactType,
    dataSourceType: state.tempInfo.mapSchema.dataSourceType,
    mappings: state.tempInfo.mapSchema.mappings,
  };
  let validationMessage = "";
  //validation
  let mappingArea = mappingObj.mappings.mappingArea;
  let mappingSegment = mappingObj.mappings.mappingSegment;
  let valuePosition = mappingObj.mappings.valuePosition;
  let occurrence = mappingObj.mappings.occurrence;

  if (mappingArea === undefined) {
    validationMessage += "Mapping Area must be selected!\n";
  }
  if (mappingSegment === undefined || mappingSegment.trim() === "") {
    validationMessage += "Mapping Segment must not be blank!\n";
  }
  if (
    isNaN(valuePosition) ||
    valuePosition === undefined ||
    valuePosition.trim() === ""
  ) {
    validationMessage +=
      "Value Position must not be blank and must be a number!\n";
  }
  if (
    isNaN(occurrence) ||
    occurrence === undefined ||
    occurrence.trim() === ""
  ) {
    validationMessage += "Occurrence must not be blank and must be a number!\n";
  }
  if (validationMessage !== "") {
    alert(validationMessage);
    return;
  }

  try {
    const response = await axios.post(
      "/mapschema/inbound/save",
      mappingObj,
      null
    );
    if (response.data) {
      alert("Saved successfully!");
      dispatch(setPopUp({ popUp: false }));
      dispatch(resetFields());
    }
  } catch (err) {}
  return false;
};

export const selectMapType = (state) => state.tempInfo.mapSchema.mapType;
export const selectConnectionId = (state) =>
  state.tempInfo.mapSchema.connectionId;
export const selectArtifactType = (state) =>
  state.tempInfo.mapSchema.artifactType;
export const selectDataSourceType = (state) =>
  state.tempInfo.mapSchema.dataSourceType;
export const selectMappings = (state) => state.tempInfo.mapSchema.mappings;
export const selectPopUp = (state) => state.tempInfo.mapSchema.popUp;
export const selectArtifactComplex = (state) =>
  state.tempInfo.mapSchema.artifactComplex;
export const selectCurrentOutboundElement = (state) =>
  state.tempInfo.mapSchema.currentOutboundElement;
export const selectCurrentArtifactDictionaryId = (state) =>
  state.tempInfo.mapSchema.currentArtifactDictionaryDataId;

export const selectIsMappingClicked = (state) =>
  state.tempInfo.mapSchema.isMappingClicked;
export const selectSelectedSchema = (state) =>
  state.tempInfo.mapSchema.selectedSchema;
export const selectOutboundSchemaTable = (state) =>
  state.tempInfo.mapSchema.outboundSchemaTable;
export const selectAllOutboundSchemaTables = (state) =>
  state.tempInfo.mapSchema.allOutboundSchemaTables;
export const selectArtifactHeaderInfo = (state) =>
  state.tempInfo.mapSchema.artifactHeaderData;
export const selectArtifactDetailInfo = (state) =>
  state.tempInfo.mapSchema.artifactDetailData;
export const selectArtifactHeaderOptionalFieldInfo = (state) =>
  state.tempInfo.mapSchema.artifactHeaderOptionalField;
export const selectArtifactDetailOptionalFieldInfo = (state) =>
  state.tempInfo.mapSchema.artifactDetailOptionalField;
export const selectArtifactHeaderConsolidatedInfo = (state) =>
  state.tempInfo.mapSchema.artifactHeaderConsolidated;
export const selectArtifactDetailConsolidatedInfo = (state) =>
  state.tempInfo.mapSchema.artifactDetailConsolidated;
export const selectLoopRowsList = (state) =>
  state.tempInfo.mapSchema.loopRowsList;
export const selectStagedOutboundEdits = (state) =>
  state.tempInfo.mapSchema.stagedOutboundEdits;

export const {
  setMapType,
  setConnectionId,
  setArtifactType,
  setDataSourceType,
  updateFields,
  setPopUp,
  setArtifactComplex,
  setArtifactDictionaryDataId,
  resetFields,
  updateOutboundElementField,
  updateOutboundElement,
  setIsMappingClicked,
  setSelectedSchema,
  setOutboundSchemaTable,
  setAllOutboundSchemaTables,
  setArtifactHeaderInfo,
  setArtifactDetailInfo,
  setArtifactHeaderOptionalFieldInfo,
  setArtifactDetailOptionalFieldInfo,
  setArtifactHeaderConsolidatedInfo,
  setArtifactDetailConsolidatedInfo,
  addOptionalField,
  setLoopRowsList,
  addItemToEditBatch,
  removeItemFromEditBatch,
  clearItemsFromEditBatch,
} = mapSchemaSlice.actions;
export default mapSchemaSlice.reducer;
